import { useContext, useEffect, useMemo, useState } from "react";
import { Box } from "@chakra-ui/react";
import type { ColDef, ICellRendererParams, RowClickedEvent } from "ag-grid-community";
import { useLocation } from "wouter";
import BaseTemplate from "../../components/BaseTemplate";
import PersistentGrid from "../../components/PersistentGrid/PersistentGrid";
import PersistentGridResetButton from "../../components/PersistentGrid/PersistentGridResetButton";
import PersistentGridClearFiltersButton from "../../components/PersistentGrid/PersistentGridClearFiltersButton";
import { PersistentGridProvider } from "../../contexts/PersistentGridContext";
import { useApi } from "../../hooks/useApi";
import { NotificationContext } from "../../components/NotificationPane";
import type { NotificationContextType, ReportOption } from "../../types";
import InternalUserRoute from "../../util/internal_user_route";
import { buildDefaultColDef, EDB_CUSTOM_withHeaderTooltips } from "../../util/AgGridFunctions";
import { useColumnDefaults } from "../../hooks/useColumnDefaults";
import { slugify } from "../../util/slugify";

// Report name rendered as a lozenge/pill, not plain text - the whole row already navigates on
// click (onRowClicked below); this is purely a visual cue that the name is the "go to report"
// affordance, matching the ActionBar button's colour so it reads as clickable the same way.
function ReportNameCellRenderer(params: ICellRendererParams<ReportOption>) {
  return (
    <Box
      as="span"
      display="block"
      w="100%"
      textAlign="left"
      boxSizing="border-box"
      px={3}
      py="4px"
      borderRadius="md"
      bg="action-bar-button-enabled"
      // Subtle top-highlight/bottom-shade overlay (colour-agnostic - works over whatever the
      // live theme's actionBarButtonEnabled colour actually is, no hardcoded hex) plus a small
      // drop shadow and inset bevel, so it reads as a raised button instead of a flat swatch.
      bgImage="linear-gradient(to bottom, rgba(255,255,255,0.25), rgba(255,255,255,0) 45%, rgba(0,0,0,0.08) 100%)"
      boxShadow="0 1px 2px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -1px 0 rgba(0,0,0,0.12)"
      color="white"
      fontSize="sm"
      fontWeight="medium"
      lineHeight="1.6"
    >
      {params.value}
    </Box>
  );
}

// Standalone (see report_runner.tsx) - no dependency on the Generic CRUD engine. Same
// PersistentGrid + click-to-navigate pattern as ProcessMapping.tsx's rule-group grid.
function ReportsList() {
  const { getReports } = useApi();
  const columnDefaults = useColumnDefaults();
  const { pushNotification } = useContext<NotificationContextType | null>(NotificationContext)!;
  const [, navigate] = useLocation();

  const [reports, setReports] = useState<ReportOption[]>([]);
  const [loading, setLoading] = useState(true);

  // Deliberately empty deps - see the matching comment in report_runner.tsx's params effect.
  // getReports/pushNotification (from useApi()/NotificationContext) are new identities every
  // render, and depending on them here caused a runaway fetch loop.
  useEffect(() => {
    async function loadReports() {
      try {
        setLoading(true);
        const data = await getReports();
        setReports(data);
      } catch {
        pushNotification("Failed to load reports.", "error");
      } finally {
        setLoading(false);
      }
    }
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columnDefs = useMemo<ColDef<ReportOption>[]>(
    () =>
      EDB_CUSTOM_withHeaderTooltips([
        { field: "reportname", headerName: "Report", ...buildDefaultColDef(columnDefaults, "reportname"), flex: 1, minWidth: 200, cellRenderer: ReportNameCellRenderer },
        // wrapText+autoHeight (not just on the grid, on THIS column specifically) grows the row
        // to fit the wrapped description text instead of clipping it to a single line - other
        // columns stay their normal single-line height since only this column wraps.
        { field: "description", headerName: "Description", ...buildDefaultColDef(columnDefaults, "description"), flex: 2, minWidth: 300, wrapText: true, autoHeight: true },
      ]),
    [columnDefaults],
  );

  const defaultColDef = useMemo<ColDef>(() => ({
    ...buildDefaultColDef(columnDefaults),
    sortable: true, resizable: true, filter: true, floatingFilter: true,
  }), [columnDefaults]);

  const handleRowClicked = (event: RowClickedEvent<ReportOption>) => {
    if (event.data) navigate(`/reports/${slugify(event.data.reportname)}`);
  };

  return (
    <InternalUserRoute>
      <PersistentGridProvider gridKey="reports-list-grid">
        <BaseTemplate fitToWindow={true}>
          <BaseTemplate.Header>
            <PersistentGridResetButton />
            <PersistentGridClearFiltersButton />
          </BaseTemplate.Header>
          <BaseTemplate.Body>
            <Box w="100%" h="100%">
              <div className="ag-theme-quartz" style={{ width: "100%", height: "100%" }}>
                <PersistentGrid<ReportOption>
                  rowData={reports}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  loading={loading}
                  // Site-wide default row height (28px, ag-grid-edb_custom.css) is too tight for
                  // the "Report" column's button-styled lozenge (padding + shadow/bevel) - override
                  // just on this grid rather than raising the global default for every other page.
                  rowHeight={36}
                  animateRows
                  onRowClicked={handleRowClicked}
                  getRowStyle={() => ({ cursor: "pointer" })}
                />
              </div>
            </Box>
          </BaseTemplate.Body>
        </BaseTemplate>
      </PersistentGridProvider>
    </InternalUserRoute>
  );
}

export default ReportsList;
